import React, { useRef } from 'react';
import Button from '$/components/base/Button';
import IconFont from '$/components/IconFont';
import { openVipModal } from '$/store/useVipModalStore';
import type { VipMembershipMode } from '$/store/useVipModalStore';
import MyCalcDetailModal from '$/components/VipModal/MyCalcDetail';
import type { MyCalcDetaiRef } from '$/components/VipModal/MyCalcDetail';
import { useOfficialStore } from '$/store/useOfficialStore';
import { useLanguageStore } from '$/store/languageStore';
import type { LanguageKeyMap } from '$/store/languageStore/smartcatTranslation';
import type { SubscriptionContract } from '@meitu/subscribe';
import { trackRoboneoVipClick } from '$/utils/vipPromptTrack';
import { getAgentTeamMembershipNameByLevel } from '$/domains/teamAgent/membershipInfo';
import ExchangeCodeModal from '../ExchangeCode';
import RenewalManagement from '../RenewalManagement';
import type { RenewalManagementModalRef } from '../RenewalManagement';
import type { ExchangeCodeRef } from '../ExchangeCode';
import InvoiceManagement from '../InvoiceManagement';
import type { InvoiceManagementModalRef } from '../InvoiceManagement';
import Image from '../../NextImage';
import calcIcon from '../radish.png';
import styles from './index.module.scss';

interface IProps {
  hide: () => void;
  contractList: SubscriptionContract[];
  vipMembershipMode?: VipMembershipMode;
}

const MEMBER_LEVEL_CONFIG = {
  // level 1 是基础会员兼容值，视觉上与 level 2 共用标准会员配色。
  1: { cardClass: 'memberCardLevel2' },
  2: { cardClass: 'memberCardLevel2' },
  3: { cardClass: 'memberCardLevel3' },
  4: { cardClass: 'memberCardLevel4' },
  5: { cardClass: 'memberCardLevel5' },
  6: { cardClass: 'memberCardLevel6' },
} as const;

type MemberLevel = keyof typeof MEMBER_LEVEL_CONFIG;
type MemberCardInfo = {
  title?: string;
  expireText?: string;
  buttonText: string;
  cardClass?: string;
};

interface MenuItem {
  title: string;
  onClick: () => void;
}

// isMemberLevel 将接口 level 收敛到当前设计稿支持的会员等级范围。
function isMemberLevel(level: number): level is MemberLevel {
  return level in MEMBER_LEVEL_CONFIG;
}

// formatMemberDate 将会员到期时间统一展示为 YYYY.MM.DD 格式。
function formatMemberDate(timestamp?: number) {
  if (!timestamp || !Number.isFinite(timestamp)) return '';
  const normalizedTimestamp = timestamp < 10_000_000_000 ? timestamp * 1000 : timestamp;
  const date = new Date(normalizedTimestamp);
  if (Number.isNaN(date.getTime())) return '';

  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}.${month}.${day}`;
}

// 渲染萝卜余额悬浮菜单，并保留旧业务弹窗的打开逻辑。
export default function OverlayContent(props: IProps) {
  const { hide, contractList, vipMembershipMode } = props;
  const exchangeCodeRef = useRef<ExchangeCodeRef>(null);
  const myCalcDetailRef = useRef<MyCalcDetaiRef>(null);
  const renewalManagementRef = useRef<RenewalManagementModalRef>(null);
  const invoiceManagementRef = useRef<InvoiceManagementModalRef>(null);
  const radishAmount = useOfficialStore((state) => state.radishAmount);
  const subscribeInfo = useOfficialStore((state) => state.userInfo.subscribe);
  const isTeamSubscribeScope = useOfficialStore((state) => Boolean(state.clientOId));
  const languageStore = useLanguageStore((state) => state.languageStore);

  // getText 封装多语言读取，统一处理会员卡中的通用文案。
  const getText = (key: keyof LanguageKeyMap) => {
    return languageStore.smartcatTranslationText((v) => v[key]);
  };

  // getMemberCardInfo 使用接口等级决定配色，并按当前 level 解析 membership_infos 展示名称。
  const getMemberCardInfo = (): MemberCardInfo => {
    const level = Number(subscribeInfo?.level) || 0;
    const isSupportedMemberLevel = isMemberLevel(level);
    const isValidMember = Boolean(subscribeInfo?.isValid && isSupportedMemberLevel);
    const expireDate = formatMemberDate(subscribeInfo?.invalidTime);
    const expireText = `${getText('credit_valid_till')}${expireDate || '--'}`;

    if (!isValidMember || !isSupportedMemberLevel) {
      return {
        title: getText('free_user'),
        expireText: getText('become_a_member_to_enjoy_more_creative_benefits_'),
        buttonText: getText('update'),
      };
    }

    const config = MEMBER_LEVEL_CONFIG[level];
    return {
      title: getAgentTeamMembershipNameByLevel(subscribeInfo?.level, subscribeInfo?.membershipInfos),
      expireText,
      buttonText: level === 6 ? getText('renewal_') : getText('update'),
      cardClass: styles[config.cardClass],
    };
  };

  // 打开会员购买弹窗，并按当前账号空间显式定位个人版或团队版。
  const handleOpenVip = () => {
    window.mtstat?.track('system_settings_click', {});
    trackRoboneoVipClick('my_carrot_upgrade');
    openVipModal(
      'topright_page',
      undefined,
      { from_source: 'my_carrot_upgrade' },
      { membershipMode: vipMembershipMode ?? (isTeamSubscribeScope ? 'team' : 'personal') },
    );
    hide?.();
  };

  // 打开订阅管理弹窗，沿用原续期管理组件能力。
  const handleOpenRenewalManagement = () => {
    hide?.();
    renewalManagementRef.current?.open();
  };

  // 打开发票管理弹窗。
  const handleOpenInvoiceManagement = () => {
    hide?.();
    invoiceManagementRef.current?.open();
  };

  // 打开算力萝卜使用明细弹窗。
  const handleOpenCalcDetail = () => {
    hide?.();
    myCalcDetailRef.current?.openMyCalcDetail();
  };

  // 打开会员兑换码弹窗，对应原 ExchangeCodeModal。
  const handleOpenExchangeCode = () => {
    hide?.();
    exchangeCodeRef.current?.open();
  };

  const menuList: MenuItem[] = [
    {
      title: getText('subscription_management'),
      onClick: handleOpenRenewalManagement,
    },
    {
      title: getText('invoice_management'),
      onClick: handleOpenInvoiceManagement,
    },
    {
      title: getText('click_to_view_the_carrot_consumption_of_each_roboneo_task'),
      onClick: handleOpenCalcDetail,
    },
    !isTeamSubscribeScope && {
      title: getText('member_redemption_code'),
      onClick: handleOpenExchangeCode,
    },
  ].filter(Boolean) as MenuItem[];

  // 渲染会员身份卡，会员等级和有效期来自 membership_info 写入的 store。
  const renderMemberCard = () => {
    const memberCardInfo = getMemberCardInfo();

    return (
      <div className={`${styles.memberCard} ${memberCardInfo.cardClass || ''}`}>
        <div className={styles.memberInfo}>
          <div className={styles.memberTitle}>
            {memberCardInfo.title}
          </div>
          {memberCardInfo.expireText ? (
            <div className={styles.memberExpire}>{memberCardInfo.expireText}</div>
          ) : null}
        </div>
        <Button
          type="default"
          size="small"
          className={styles.upgradeButton}
          onClick={handleOpenVip}
        >
          {memberCardInfo.buttonText}
        </Button>
      </div>
    );
  };

  // 渲染总算力萝卜数量，数值来源于接口 total_amount。
  const renderRadishSummary = () => {
    return (
      <div className={styles.radishSummary}>
        <Image src={calcIcon} width={20} height={20} alt="" />
        <div className={styles.radishContent}>
          <div className={styles.radishTitle}>{getText('302')}</div>
          <div className={styles.radishAmount}>{radishAmount}</div>
        </div>
      </div>
    );
  };

  // 渲染新版菜单项，点击后打开对应旧业务弹窗。
  const renderMenuItem = (item: MenuItem) => {
    return (
      <div className={styles.menuItem} onClick={item.onClick} key={item.title}>
        <div className={styles.menuTitle}>{item.title}</div>
        <IconFont name="chevronRightFill" color="var(--content-quaternary)" fontSize={10} />
      </div>
    );
  };

  return (
    <div className={styles.popoverContent}>
      {renderMemberCard()}
      <div className={styles.divider} />
      {renderRadishSummary()}
      <div className={styles.divider} />
      <div className={styles.menuList}>{menuList.map(renderMenuItem)}</div>
      <ExchangeCodeModal ref={exchangeCodeRef} />
      <MyCalcDetailModal ref={myCalcDetailRef} />
      <RenewalManagement ref={renewalManagementRef} contractList={contractList} />
      <InvoiceManagement ref={invoiceManagementRef} />
    </div>
  );
}
